// Client-error telemetry. An uncaught error in the BROWSER (a React render/effect crash caught by the
// route error boundary, or a global `error` / `unhandledrejection`) is reported back to the server by
// the client and lands here as its OWN wide event — `name = 'client_error'`, so browser crashes never
// contaminate the API `request` metrics.
//
// Correlation is the whole point. The event carries the SSR render's `trace.id` (so it links to the
// `pageview` that produced the page and the `request` events that page made) and the per-tab
// `session.id` (so the user's trail across the visit is reconstructable). Those ride in as request
// headers on the ingest call — the browser can't write to the queue directly. Reuses the OTel
// `exception.*` shape so the same query that finds server exceptions finds these too.
import { randomUUID } from 'node:crypto';
import { buildWideEvent, type WideEvent } from './event';
import { type ClientEventContext, clientEventFields } from './client-event';
import { bufferWideEvent } from './buffer';
import { wideEventsEnabled } from './enabled';
import { normalizePagePath } from './url';

export const CLIENT_ERROR_EVENT_NAME = 'client_error';

/**
 * Where in the client the error was caught, ordered by how much the visitor saw:
 *
 * - `uncaught`: React's `onUncaughtError` — a fault it could NOT recover from and no boundary caught.
 *   The page is broken for the visitor. The most serious value here.
 * - `boundary`: a React route error boundary. The visitor gets our error screen instead of the page.
 * - `unhandledrejection`: a rejected promise with no catch. Nothing crashes visibly, so the damage is
 *   whatever that promise was doing — often one action failing silently.
 * - `global`: a window 'error' event, outside React. Frequently third-party or extension code.
 * - `hydration`: the server HTML and the first client render disagreed, and React repaired the DOM by
 *   itself. High volume, low impact: a wrong value on screen, not a broken page.
 * - `recoverable`: any other fault React recovered from on its own. The lowest impact of the six.
 *
 * `hydration` and `uncaught` are split OUT of `recoverable`, which used to carry all three: the portals
 * client distinguished them in the log line's prefix but sent one `source` for all of them, so the
 * value meaning "the page is broken" was indistinguishable from the value meaning "a counter flickered".
 * It is a ticket field now (TEC-3748), which makes the distinction load-bearing.
 *
 * One list, because there are three consumers that must agree: this event, the zod enum validating the
 * browser's report (`portals/src/lib/data.ts`), and the Jira select option. That enum has no `.catch()`,
 * so a value sent but not listed fails validation and loses the whole report.
 */
export const CLIENT_ERROR_SOURCES = [
	'uncaught',
	'boundary',
	'unhandledrejection',
	'global',
	'hydration',
	'recoverable',
] as const;

export type ClientErrorSource = (typeof CLIENT_ERROR_SOURCES)[number];

/**
 * A single user-action breadcrumb leading up to the error. Kept tiny and already bounded/sanitized by
 * the client; stored as a JSON string so a consumer doesn't infer a column per breadcrumb shape.
 */
export interface ClientErrorBreadcrumb {
	t: number;
	type: string;
	message: string;
}

/** What only the browser can tell us. Everything else is server-derived; see `ClientEventContext`. */
export interface RecordClientErrorInput extends ClientEventContext {
	route: string;
	name: string;
	message: string;
	stack?: string;
	/**
	 * The error's `cause` chain, kept out of `stack` on purpose: `stack` is a fingerprint input, so
	 * appending to it re-tickets already-open issues. See `describeCauseChain` in portals' `data.ts`.
	 */
	cause?: string;
	/**
	 * On a hydration-mismatch report, the paired server-text/client-value of each visible counter. Its
	 * own field for the same fingerprint reason as `cause`, and because riding in `breadcrumbs` once cost
	 * whole reports to the array bound. See `counterSnapshot` in portals' `client.tsx`.
	 */
	counters?: string;
	/**
	 * `url:line:col` of the script that failed, from the `ErrorEvent`. The only locator a parse-time
	 * `SyntaxError` has: it never ran, so it carries no stack and no frame to attribute it with.
	 *
	 * Its own field for the same fingerprint reason as `cause` — and because a browser-reported
	 * position is not a stack frame and should not be dressed as one. See `scriptErrorLocation` in
	 * portals' `script-error-location.ts`.
	 */
	script?: string;
	/**
	 * On a hydration text mismatch, the text that actually changed, as `"server" -> "client"` pairs.
	 *
	 * React cannot tell us this. In production it builds `#418` as
	 * `formatProdErrorMessage(418, isText ? 'text' : 'HTML', '')` — the argument that carries the diff in
	 * development is a hard-coded empty string, which is the trailing `args[]=` on every one of these
	 * tickets. Recovered instead from portals' `characterData` recorder, which witnesses React's own
	 * repair. See `describeTextMismatch` in `hydration-rewrite-monitor.ts`.
	 *
	 * Its own field for the same fingerprint reason as `cause`, and because it must not crowd out the
	 * counter snapshot inside `counters`' much tighter bound.
	 */
	mismatch?: string;
	source?: ClientErrorSource;
	breadcrumbs?: ClientErrorBreadcrumb[];
}

/**
 * Buffer one `client_error` event and return it, so the caller can also print it to the log the ticket
 * parser reads. Returns undefined when telemetry is disabled or the build failed. Never throws —
 * reporting a crash must never itself surface an error.
 */
export function recordClientError(input: RecordClientErrorInput): WideEvent | undefined {
	if (!wideEventsEnabled()) return undefined;
	try {
		const event = buildWideEvent({
			name: CLIENT_ERROR_EVENT_NAME,
			attributes: {
				...clientEventFields(input),
				// Countable like a failed request, so the shared error views pick it up.
				'error': true,
				'exception.type': input.name,
				'exception.message': input.message,
				'exception.stacktrace': input.stack ?? null,
				'exception.cause': input.cause ?? null,
				'exception.script': input.script ?? null,
				'client.hydration_mismatch': input.mismatch ?? null,
				'client.counters': input.counters ?? null,
				// Collapse ids to the route shape (same rule as every other event) so a client error
				// groups per route instead of per visited id.
				'url.path': normalizePagePath(input.route),
				'client.error.source': input.source ?? null,
				// A leaf span in the trace, parented to the render's root span.
				'span.id': randomUUID(),
				'http.referer': input.referer ?? null,
				...input.browser,
				'client.breadcrumbs':
					input.breadcrumbs && input.breadcrumbs.length > 0 ? JSON.stringify(input.breadcrumbs) : null,
				'client.breadcrumb_count': input.breadcrumbs?.length ?? null,
			},
		});
		bufferWideEvent(event);
		return event;
	} catch {
		// Telemetry is best-effort; a failed report must not propagate into the ingest handler.
		return undefined;
	}
}
